import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { BaseLink } from "shared/components/common/base-link";
import { fontMerriweather, theme } from "shared/lib/styles";

import IconAlertOn from "./alert-on.svg";
import IconAlert from "./alert.svg";

export const Container = styled.div`
  position: relative;

  padding-block-start: 67px;

  overflow: hidden;
  ${theme.breakpoints.up("tablet")} {
    padding-block-start: 64px;
  }
`;

export const TextWrapper = styled.div`
  margin-block: 0;
  margin-inline: auto;
  padding-block: 0;
  padding-inline: 20px;
`;

export const Title = styled.div`
  margin-block-end: 8px;

  font-weight: 900;
  font-size: 20px;
  font-family: ${fontMerriweather};
  line-height: 24px;
  text-align: center;
  ${theme.breakpoints.up("tablet")} {
    margin-block-end: 20px;

    font-size: 32px;
    line-height: 38.4px;
  }
`;

export const Points = styled.ul`
  max-inline-size: 335px;
  margin-block: 0;
  margin-block-end: 160px;
  margin-inline: auto;

  text-align: center;

  list-style: disc;
  list-style: inside;
  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 716px;
    margin-block-end: 210px;
  }
`;

export const Point = styled.li`
  color: #001246;
  font-weight: 300;
  font-size: 15px;
  line-height: 18px;
  ${theme.breakpoints.up("tablet")} {
    font-size: 20px;
    line-height: 26px;
  }
`;

export const PointLink = styled.a`
  color: #4585ff;
  font-weight: 400;
  text-decoration: underline;

  cursor: pointer;
`;

export const AlertContainer = styled.div`
  position: absolute;
  inset-block-start: 228px;
  inset-inline-start: 50%;

  display: flex;

  inline-size: max-content;

  transform: translateX(-50%);
  ${theme.breakpoints.up("tablet")} {
    inset-block-start: 239px;
  }
`;

export const SvgAlert = styled(IconAlert)`
  circle {
    stroke-width: 6;
  }
`;
export const SvgAlertOn = styled(IconAlertOn)`
  circle {
    stroke-width: 6;
  }
`;

type AlertProps = {
  $isActive?: boolean;
};

export const Alert = styled.div<AlertProps>`
  position: relative;

  background-size: contain;

  @keyframes lightOn1 {
    0% {
      opacity: 0;
    }
    6.5% {
      opacity: 0;
    }
    13.67% {
      opacity: 1;
    }
    16.67% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes lightOn2 {
    0% {
      opacity: 0;
    }
    33.3% {
      opacity: 0;
    }
    47% {
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    83.3% {
      opacity: 0;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes lightOn3 {
    0% {
      opacity: 0;
    }
    66.67% {
      opacity: 0;
    }
    80.3% {
      opacity: 1;
    }
    83.3% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  ${props =>
    props.$isActive
      ? css`
          &:nth-child(2),
          &:nth-child(5),
          &:nth-child(8) {
            svg:nth-child(2) {
              animation: lightOn1 11s ease-in-out infinite;
            }
          }
          &:nth-child(3),
          &:nth-child(6),
          &:nth-child(9) {
            svg:nth-child(2) {
              animation: lightOn3 11s ease-in-out infinite;
            }
          }
          &:nth-child(1),
          &:nth-child(4),
          &:nth-child(7) {
            svg:nth-child(2) {
              animation: lightOn2 11s ease-in-out infinite;
            }
          }
        `
      : null};

  &:not(:last-child) {
    margin-inline-end: 8px;
    ${theme.breakpoints.up("tablet")} {
      margin-inline-end: 17px;
    }
  }
  svg {
    inline-size: 140px;
    block-size: 140px;
    &:nth-child(2) {
      position: absolute;
      inset-block-start: 0;
      inset-inline-start: 0;

      opacity: 0;
    }
    ${theme.breakpoints.up("tablet")} {
      inline-size: 180.8px;
      block-size: 180.8px;
    }
    ${theme.breakpoints.up("desktop")} {
      inline-size: 226px;
      block-size: 226px;
    }
  }
`;

export const ImageContainer = styled.div`
  position: relative;

  max-inline-size: 275px;
  margin-block: 159px 0;
  margin-inline: auto;
  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 454px;
    margin-block: -20px 0;
    margin-inline: auto;
  }
  ${theme.breakpoints.up("desktop")} {
    margin-block: 191px 0;
    margin-inline: auto;
  }
`;

type ToggleProps = {
  $isActive?: boolean;
};

export const Toggle = styled.div<ToggleProps>`
  position: absolute;
  inset-block-start: 49.5%;
  inset-inline-start: 76.5%;

  inline-size: 32.4px;
  block-size: 21px;

  border-radius: 45px;

  background: #dddddd;

  @keyframes toggleBgSwitch {
    0% {
      background: #dddddd;
    }
    6.5%,
    100% {
      background-color: #4585ff;
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation: toggleBgSwitch 11s linear infinite;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    inline-size: 56px;
    block-size: 34px;
  }
`;

type ToggleCircleProps = {
  $isActive?: boolean;
};

export const ToggleCircle = styled.div<ToggleCircleProps>`
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: 5%;

  inline-size: 17.5px;
  block-size: 17.5px;

  border-radius: 50%;

  background-color: #ffffff;

  transform: translateY(-50%);

  @keyframes toggleSlide {
    0% {
      inset-inline-start: 5%;
    }
    6.5%,
    100% {
      inset-inline-start: 42%;
    }
  }

  ${props =>
    props.$isActive
      ? css`
          animation: toggleSlide 11s linear infinite;
        `
      : null};

  ${theme.breakpoints.up("tablet")} {
    inline-size: 30px;
    block-size: 30px;
  }
`;

export const ImagePhone = styled.img`
  display: block;

  inline-size: 100%;
`;

export const ButtonContainer = styled.div`
  padding-block: 32px;
  padding-inline: 20px;

  background: #fafafa;
  ${theme.breakpoints.up("tablet")} {
    padding-block: 64px;
    padding-inline: 0;
  }
`;

const buttonStyle = () => css`
  display: block;

  inline-size: 270px;
  margin-block: 0;
  margin-inline: auto;
  padding-block: 16px;
  padding-inline: 0;

  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
  text-transform: uppercase;

  border-radius: 4px;

  background: #001246;

  cursor: pointer;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 260px;
    padding-block: 15px 15.75px;
    padding-inline: 0;

    font-size: 16px;
    line-height: 19px;
  }
`;

export const Button = styled.button`
  ${buttonStyle()}
`;

export const BaseLinkButton = styled(BaseLink)`
  ${buttonStyle()}
`;
